import React, { useState, useEffect } from 'react';
import { X, FileText } from 'lucide-react';
import LoadingSpinner from '../ui/LoadingSpinner';

const SO_NUMBER_LENGTH = 10;

function formatSoNumber(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length === 0) return '';
  if (digits.length > SO_NUMBER_LENGTH) return digits.slice(0, SO_NUMBER_LENGTH);
  return digits.padStart(SO_NUMBER_LENGTH, '0');
}

function SoNumberInput({ id, label, value, onChange, disabled, autoFocus, hint }) {
  return (
    <div className="mb-3">
      <label htmlFor={id} className="block text-locate2-medium-gray text-sm font-medium mb-1">
        {label}
      </label>
      <input
        id={id}
        type="text"
        inputMode="numeric"
        value={value}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, '');
          if (digits.length <= SO_NUMBER_LENGTH) onChange(digits);
        }}
        placeholder="e.g. 79448"
        maxLength={SO_NUMBER_LENGTH}
        disabled={disabled}
        autoFocus={autoFocus}
        className="w-full px-4 py-3 border border-locate2-border-light rounded-lg text-locate2-medium-gray font-mono text-lg tracking-wider placeholder:text-locate2-muted placeholder:text-base placeholder:tracking-normal focus:outline-none focus:border-locate2-primary focus:ring-1 focus:ring-locate2-primary transition-colors disabled:opacity-60 disabled:bg-locate2-bg-light disabled:cursor-not-allowed"
      />
      {hint && <p className="text-locate2-light-gray text-xs mt-1">{hint}</p>}
    </div>
  );
}

export default function SetSoNumberModal({
  isOpen,
  onClose,
  onSubmit,
  deliveryName,
  operationType = '',
  existingSapSoNumber = '',
  existingPickupSapSoNumber = '',
  isProcessing,
}) {
  const isExchange = operationType === 'Exchange';

  const [dropSo, setDropSo] = useState('');
  const [pickupSo, setPickupSo] = useState('');
  const [error, setError] = useState('');

  // When the modal opens (or the delivery behind it changes), seed any
  // pre-existing leg values so the operator only enters the missing one.
  useEffect(() => {
    if (isOpen) {
      setDropSo(existingSapSoNumber ? String(existingSapSoNumber).replace(/\D/g, '') : '');
      setPickupSo(existingPickupSapSoNumber ? String(existingPickupSapSoNumber).replace(/\D/g, '') : '');
      setError('');
    }
  }, [isOpen, existingSapSoNumber, existingPickupSapSoNumber]);

  if (!isOpen) return null;

  const dropPreset = !!existingSapSoNumber;
  const pickupPreset = !!existingPickupSapSoNumber;

  const handleClose = () => {
    setDropSo('');
    setPickupSo('');
    setError('');
    onClose();
  };

  const handleSubmit = () => {
    if (isExchange) {
      if (!dropSo.trim() || !pickupSo.trim()) {
        setError('Both Drop SO and Pickup SO are required');
        return;
      }
      setError('');
      onSubmit({
        sapSoNumber: formatSoNumber(dropSo),
        pickupSapSoNumber: formatSoNumber(pickupSo),
      });
      return;
    }

    if (!dropSo.trim()) {
      setError('Please enter a SAP SO Number');
      return;
    }
    setError('');
    onSubmit({ sapSoNumber: formatSoNumber(dropSo) });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !isProcessing) handleSubmit();
  };

  const dropLabel = isExchange ? 'Drop SO Number' : 'SAP SO Number';
  const dropPreview = dropSo && !dropPreset ? formatSoNumber(dropSo) : '';
  const pickupPreview = pickupSo && !pickupPreset ? formatSoNumber(pickupSo) : '';

  const submitDisabled = isProcessing
    || !dropSo.trim()
    || (isExchange && !pickupSo.trim());

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onKeyDown={handleKeyDown}>
        <div className="flex items-center justify-between p-4 border-b border-locate2-border-light">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-status-planned-bg flex items-center justify-center">
              <FileText size={16} className="text-status-planned" />
            </div>
            <h3 className="text-lg font-semibold text-locate2-dark-blue">
              {isExchange ? 'Set Exchange SO Numbers' : 'Set SAP SO Number'}
            </h3>
          </div>
          <button
            onClick={handleClose}
            disabled={isProcessing}
            className="text-locate2-light-gray hover:text-locate2-medium-gray transition-colors disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-4">
          <p className="text-locate2-light-gray text-sm mb-4">
            Enter the manually created SO Number{isExchange ? 's' : ''} for{' '}
            <span className="font-medium text-locate2-medium-gray">{deliveryName}</span>
          </p>

          <SoNumberInput
            id="so-drop"
            label={dropLabel}
            value={dropSo}
            onChange={setDropSo}
            disabled={isProcessing || dropPreset}
            autoFocus={!dropPreset}
            hint={dropPreset ? 'Already set — read-only' : (dropPreview ? `Will be submitted as ${dropPreview}` : null)}
          />

          {isExchange && (
            <SoNumberInput
              id="so-pickup"
              label="Pickup SO Number"
              value={pickupSo}
              onChange={setPickupSo}
              disabled={isProcessing || pickupPreset}
              autoFocus={dropPreset && !pickupPreset}
              hint={pickupPreset ? 'Already set — read-only' : (pickupPreview ? `Will be submitted as ${pickupPreview}` : null)}
            />
          )}

          {error && <p className="text-status-error text-sm mt-2">{error}</p>}
        </div>
        <div className="flex gap-3 p-4 border-t border-locate2-border-light">
          <button
            onClick={handleClose}
            disabled={isProcessing}
            className="flex-1 px-4 py-2.5 border border-locate2-border rounded-lg text-locate2-medium-gray font-medium hover:bg-locate2-bg-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitDisabled}
            className="flex-1 px-4 py-2.5 bg-status-planned rounded-lg text-white font-medium hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          >
            {isProcessing ? (
              <>
                <LoadingSpinner size="sm" className="border-white border-t-transparent" />
                Submitting...
              </>
            ) : (
              isExchange ? 'Set SO Numbers' : 'Set SO Number'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
